import * as React from 'react'
import { useRegisterUnsavedChanges, useUnsavedChanges } from '@/components/visitone/unsaved-changes'
import type { SaveState } from '@/components/visitone/save-bar'
import type { ErrorSummaryItem } from '@/components/visitone/error-summary'
import { useT } from '@/components/visitone/visitone-provider'
import {
  countByLanguage,
  countBySection,
  dirtyKeys,
  fieldForKey,
  FormSaveError,
  orderErrorKeys,
  parseKey,
  type FieldValue,
  type FormDefinition,
  type FormValues,
} from '@/lib/forms/form-state'

/*
 * State for one configuration form (VIS-321). Changes stay unsaved until Save
 * or Discard, are counted against the saved values, and are registered with
 * the shared guard so every way of leaving asks first. Validation runs on
 * blur and on Save and never changes what was entered. A save that fails
 * keeps every change unsaved.
 */
export function useConfigurationForm({
  definition,
  initialValues,
  subject,
  onSave,
  readOnly = false,
}: {
  definition: FormDefinition
  initialValues: FormValues
  subject: string
  /** Rejects with `FormSaveError` for errors on fields; any other rejection is a failed save. */
  onSave: (values: FormValues) => Promise<void>
  readOnly?: boolean
}) {
  const t = useT()
  const { confirmThen } = useUnsavedChanges()
  const languages = React.useMemo(() => definition.contentLanguages ?? [], [definition.contentLanguages])

  const [baseline, setBaseline] = React.useState(initialValues)
  const [values, setValues] = React.useState(initialValues)
  const [language, setLanguage] = React.useState(languages[0]?.value ?? '')
  const [touched, setTouched] = React.useState<ReadonlySet<string>>(() => new Set())
  const [submitted, setSubmitted] = React.useState(false)
  const [saveState, setSaveState] = React.useState<SaveState>('idle')
  const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({})
  const [serverMessages, setServerMessages] = React.useState<string[]>([])
  const [invalidAttempt, setInvalidAttempt] = React.useState(0)

  const mounted = React.useRef(true)
  React.useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const dirty = React.useMemo(() => (readOnly ? [] : dirtyKeys(values, baseline)), [readOnly, values, baseline])
  const dirtyBySection = React.useMemo(() => countBySection(definition, dirty), [definition, dirty])
  const dirtyByLanguage = React.useMemo(() => countByLanguage(dirty), [dirty])

  const errors = React.useMemo(() => {
    const messages: Record<string, string> = { ...serverErrors }
    for (const [key, messageKey] of Object.entries(definition.validate(values))) messages[key] = t(messageKey)
    return messages
  }, [definition, values, serverErrors, t])

  const visibleErrors = React.useMemo(() => {
    const shown: Record<string, string> = {}
    for (const [key, message] of Object.entries(errors)) {
      if (submitted || touched.has(key) || key in serverErrors) shown[key] = message
    }
    return shown
  }, [errors, submitted, touched, serverErrors])

  const languageLabel = React.useCallback(
    (value: string) => languages.find((entry) => entry.value === value)?.label ?? value,
    [languages],
  )

  const labelFor = React.useCallback(
    (key: string) => {
      const field = fieldForKey(definition, key)
      const { language: keyLanguage } = parseKey(key)
      if (!field) return key
      return keyLanguage && languages.length > 1
        ? t('forms.field.withLanguage', { label: field.label, language: languageLabel(keyLanguage) })
        : field.label
    },
    [definition, languages.length, languageLabel, t],
  )

  const summary = React.useMemo<ErrorSummaryItem[]>(
    () =>
      submitted
        ? orderErrorKeys(definition, errors).map((key) => ({ key, label: labelFor(key), message: errors[key] }))
        : [],
    [submitted, definition, errors, labelFor],
  )

  const setValue = React.useCallback((key: string, value: FieldValue) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    setServerErrors((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
    setSaveState((prev) => (prev === 'saving' ? prev : 'idle'))
  }, [])

  const blur = React.useCallback((key: string) => {
    setTouched((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
  }, [])

  const reset = React.useCallback(() => {
    setValues(baseline)
    setSubmitted(false)
    setTouched(new Set())
    setServerErrors({})
    setServerMessages([])
    setSaveState((prev) => (prev === 'saving' ? prev : 'idle'))
  }, [baseline])

  // ANALYTICS-REVIEW: instrumentation plan needed before shipping (save, save failed, discard, leave confirmed).
  const save = React.useCallback(async () => {
    if (readOnly || saveState === 'saving') return
    if (Object.keys(definition.validate(values)).length > 0) {
      setSubmitted(true)
      setInvalidAttempt((n) => n + 1)
      return
    }
    const snapshot = values
    setServerErrors({})
    setServerMessages([])
    setSaveState('saving')
    try {
      await onSave(snapshot)
      if (!mounted.current) return
      setBaseline(snapshot)
      setSubmitted(false)
      setTouched(new Set())
      setSaveState('saved')
    } catch (error) {
      if (!mounted.current) return
      if (error instanceof FormSaveError) {
        const mapped: Record<string, string> = {}
        const unmapped = [...error.messages]
        for (const [key, message] of Object.entries(error.fieldErrors)) {
          if (key in snapshot) mapped[key] = message
          else unmapped.push(message)
        }
        setServerErrors(mapped)
        setServerMessages(unmapped)
        setSubmitted(true)
        setSaveState('idle')
        setInvalidAttempt((n) => n + 1)
      } else {
        setSaveState('failed')
      }
    }
  }, [readOnly, saveState, definition, values, onSave])

  const requestDiscard = React.useCallback(
    (focusAfterDiscard?: HTMLElement | null) => confirmThen({ kind: 'discard' }, reset, { confirmFocus: focusAfterDiscard }),
    [confirmThen, reset],
  )

  const otherLanguages = languages
    .filter((entry) => entry.value !== language && (dirtyByLanguage[entry.value] ?? 0) > 0)
    .map((entry) => ({ language: entry.label, count: dirtyByLanguage[entry.value] }))

  useRegisterUnsavedChanges(
    readOnly || dirty.length === 0 ? null : { count: dirty.length, subject, otherLanguages, discard: reset },
  )

  // Ctrl/Cmd+S uses the same save path as the save bar.
  const saveRef = React.useRef(save)
  React.useLayoutEffect(() => {
    saveRef.current = save
  })
  const hasChanges = dirty.length > 0
  React.useEffect(() => {
    if (!hasChanges) return
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        void saveRef.current()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [hasChanges])

  return {
    values,
    baseline,
    language,
    setLanguage,
    languages,
    languageLabel,
    setValue,
    blur,
    dirty,
    dirtyBySection,
    dirtyByLanguage,
    errors: visibleErrors,
    summary,
    serverMessages: submitted ? serverMessages : [],
    submitted,
    invalidAttempt,
    saveState,
    save,
    reset,
    requestDiscard,
    readOnly,
  }
}

export type ConfigurationForm = ReturnType<typeof useConfigurationForm>
